import React, { useEffect, useCallback, useContext } from 'react';
import I18n from 'i18n-js';

const configureTranslation = translations => {
  I18n.translations = translations;
};
const LocaleContext = /*#__PURE__*/React.createContext({
  locale: null
});
const ChangeLocaleContext = /*#__PURE__*/React.createContext({
  changeLocale: null
});
const I18nProvider = ({
  children,
  locale,
  changeLocale
}) => {
  useEffect(() => {
    I18n.locale = locale;
    I18n.defaultLocale = locale;
  }, [locale]);
  return /*#__PURE__*/React.createElement(LocaleContext.Provider, {
    value: {
      locale
    }
  }, /*#__PURE__*/React.createElement(ChangeLocaleContext.Provider, {
    value: {
      changeLocale
    }
  }, children));
};

const useTranslate = () => {
  const locale = useCurrentLocaleValue();
  const translate = useCallback((translationKey, options) => {
    return I18n.t(translationKey, Object.assign({
      locale
    }, options));
  }, [locale]);
  return translate;
};
const useDateTimeFormat = () => {
  const locale = useCurrentLocaleValue();
  const format = useCallback(
  // TODO: let dateFormat type be defined externally by augmentation
  (date, dateFormat, options) => {
    return I18n.l(dateFormat, date, Object.assign({
      locale
    }, options));
  }, [locale]);
  return format;
};
const useCurrentLanguage = () => {
  const locale = useCurrentLocaleValue();
  const {
    changeLocale
  } = useContext(ChangeLocaleContext);
  if (!locale || !changeLocale) throw new Error('Must be used under I18nProvider');
  return {
    currentLanguage: locale,
    changeLanguage: changeLocale
  };
};
const useCurrentLocaleValue = () => {
  const {
    locale
  } = useContext(LocaleContext);
  if (!locale) throw new Error('Must be used under I18nProvider');
  return locale;
};
function substringOnce(message, separator) {
  if (message.includes(separator)) {
    const startIndex = message.indexOf(separator);
    const first = message.substring(0, startIndex);
    const second = message.substring(startIndex + separator.length);
    return [first, second];
  } else {
    return [];
  }
}
function resolveComponents(message, components = {}) {
  const nodes = [];
  let messageToParse = message;
  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const compName in components) {
    while (messageToParse && messageToParse.includes(`<${compName}>`)) {
      const Comp = components[compName];
      const [beforeText, textWithChildren] = substringOnce(messageToParse, `<${compName}>`);
      nodes.push(beforeText);
      const [childrenText, restOfMessage] = substringOnce(textWithChildren, `</${compName}>`);
      nodes.push( /*#__PURE__*/React.createElement(Comp, null, childrenText));
      messageToParse = restOfMessage;
    }
  }
  if (messageToParse) {
    nodes.push(messageToParse);
  }
  return nodes;
}
const useTranslateWithFormatting = () => {
  const locale = useCurrentLocaleValue();
  return useCallback((translationKey, options) => {
    const translation = I18n.t(translationKey, Object.assign({
      locale
    }, options));
    if (!(options === null || options === void 0 ? void 0 : options.components)) {
      return translation;
    }
    return resolveComponents(translation, options === null || options === void 0 ? void 0 : options.components);
  }, [locale]);
};

const getDefaultBrowserLocale = () => {
  const lang = navigator.language;
  return lang.includes('-') ? lang.split('-')[0] : lang;
};

export { I18nProvider, configureTranslation, getDefaultBrowserLocale, useCurrentLanguage, useDateTimeFormat, useTranslate, useTranslateWithFormatting };
